import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Badge, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import SearchableSelect from '../../components/ui/SearchableSelect';
import FileViewerModal from '../../components/workspace/FileViewerModal';
import QuickUploadModal from '../../components/workspace/QuickUploadModal';
import LimitReachedModal from '../../components/workspace/LimitReachedModal';
import ExpirationField from '../../components/workspace/ExpirationField';
import RetryClassificationButton from '../../components/workspace/RetryClassificationButton';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * MyDocsPanel
 *
 * Personal document management for any team member (admin or client).
 * Shows ONLY documents this user uploaded themselves, grouped by status:
 *   - Needs your attention (unmatched + rejected)
 *   - In review
 *   - Approved
 *
 * Members can: classify unmatched docs, reclassify any doc, replace files,
 * delete their own docs. Reclassifying or replacing always sets status back
 * to 'in_review' so staff can re-verify.
 */
export default function MyDocsPanel() {
  const { teamId, team, membership, role } = useWorkspace();
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const isMobile = useIsMobile();
  const sectionGap = isMobile ? 16 : 24;

  const [uploads, setUploads]     = useState([]);
  const [templates, setTemplates] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]); // templates assigned to my role
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [viewing, setViewing]     = useState(null); // doc being viewed in modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [limitHit, setLimitHit] = useState(null); // { used, limit, plan } when 403 from backend

  // Background batch progress (mirrored up from QuickUploadModal, which stays
  // mounted while hidden so its upload loop survives the instant close).
  const [batchItems, setBatchItems] = useState([]);
  const batchApiRef = useRef(null); // { retry(id), dismiss(id) }

  const memberId = membership?.id;

  const refresh = useCallback(async () => {
    if (!teamId || !memberId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [upRes, tplRes, assignRes] = await Promise.allSettled([
        supabase
          .from('document_uploads')
          .select('*')
          .eq('team_id', teamId)
          .eq('member_id', memberId)
          .order('created_at', { ascending: false }),
        supabase
          .from('document_templates')
          .select('id, label, label_es, key, category, required, has_expiration, sort_order')
          .eq('team_id', teamId)
          .order('sort_order', { ascending: true })
          .order('label', { ascending: true }),
        membership?.role_id
          ? supabase
              .from('template_role_assignments')
              .select('template_id')
              .eq('team_id', teamId)
              .eq('role_id', membership.role_id)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (upRes.status === 'rejected' || upRes.value?.error) {
        throw upRes.reason || upRes.value?.error || new Error('Could not load your documents.');
      }
      setUploads(upRes.value.data || []);
      setTemplates(tplRes.status === 'fulfilled' ? (tplRes.value?.data || []) : []);
      setRoleAssignments(assignRes.status === 'fulfilled' ? (assignRes.value?.data || []) : []);
    } catch (err) {
      console.error('My docs load failed:', err);
      setError(err.message || 'Failed to load your documents.');
    } finally {
      setLoading(false);
    }
  }, [teamId, memberId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-poll while anything is in flight: an active client-side batch, or a
  // recent unmatched upload the AI may still be classifying in n8n.
  useEffect(() => {
    const clientActive = batchItems.some(b => b.status === 'queued' || b.status === 'uploading');
    const hasRecentUnmatched = uploads.some(u => {
      if (u.status === 'rejected') return false;
      if (u.template_id) return false;
      const age = (Date.now() - new Date(u.created_at).getTime()) / 1000;
      return age < 300;
    });
    if (!clientActive && !hasRecentUnmatched) return;
    const t = setInterval(() => refresh(), 3000);
    return () => clearInterval(t);
  }, [uploads, batchItems, refresh]);

  // ── Derived buckets ──
  const buckets = useMemo(() => {
    const inProgress = [];
    const unmatched = [];
    const rejected  = [];
    const inReview  = [];
    const approved  = [];
    const now = Date.now();
    uploads.forEach(u => {
      if (u.status === 'rejected')          rejected.push(u);
      else if (!u.template_id) {
        // Fresh docs the AI is still working on live in "In progress"; the age
        // guard keeps legacy stuck-pending rows in Unmatched where they can be
        // classified manually.
        const cs = u.classification_status;
        const age = (now - new Date(u.created_at).getTime()) / 1000;
        if ((cs === 'pending' || cs === 'classifying') && age < 600) inProgress.push(u);
        else unmatched.push(u);
      }
      else if (u.status === 'approved')     approved.push(u);
      else if (u.status === 'in_review')    inReview.push(u);
    });
    return { inProgress, unmatched, rejected, inReview, approved };
  }, [uploads]);

  // Client-side batch entries still worth showing: everything not yet handed
  // over to a DB row ('done' items are covered by their inserted row).
  const pendingClientItems = useMemo(
    () => batchItems.filter(b => b.status !== 'done'),
    [batchItems]
  );
  const hasInProgress = pendingClientItems.length > 0 || buckets.inProgress.length > 0;

  // Templates expected of THIS user's role that haven't been uploaded yet.
  // (Skips rejected ones — a rejected doc means they still owe a re-upload,
  // so we don't double-count it.)
  const missingTemplates = useMemo(() => {
    if (!membership?.role_id || roleAssignments.length === 0) return [];
    const assignedIds = new Set(roleAssignments.map(r => r.template_id));
    const uploadedTplIds = new Set(
      uploads
        .filter(u => u.template_id && u.status !== 'rejected')
        .map(u => u.template_id)
    );
    return templates.filter(t =>
      assignedIds.has(t.id) && !uploadedTplIds.has(t.id)
    );
  }, [membership, roleAssignments, templates, uploads]);

  const tplById = useMemo(
    () => Object.fromEntries(templates.map(t => [t.id, t])),
    [templates]
  );

  // ── Actions ──
  const classify = async (uploadId, templateId) => {
    const tpl = tplById[templateId];
    if (!tpl) return;
    try {
      const { data, error: err } = await supabase
        .from('document_uploads')
        .update({
          template_id: templateId,
          display_name: tpl.label,
          status: 'in_review',
        })
        .eq('id', uploadId)
        .select()
        .single();
      if (err) throw err;
      setUploads(prev => prev.map(u => u.id === uploadId ? data : u));
    } catch (err) {
      console.error('Classify failed:', err);
      toast.error('Could not classify: ' + (err.message || 'unknown error'));
    }
  };

  const remove = async (upload) => {
    const r = await confirm({
      title: 'Delete document',
      message: `Delete "${upload.original_filename || 'this document'}"? This can't be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;
    try {
      // Delete the storage object first (best effort — non-fatal if missing)
      if (upload.storage_path) {
        const path = upload.storage_path
          .replace(/^\/+/, '')
          .replace(/^client-docs\//, '');
        await supabase.storage.from('client-docs').remove([path]);
      }
      const { error: delErr } = await supabase
        .from('document_uploads')
        .delete()
        .eq('id', upload.id);
      if (delErr) throw delErr;
      setUploads(prev => prev.filter(u => u.id !== upload.id));
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Could not delete: ' + (err.message || 'unknown error'));
    }
  };

  const replace = async (upload, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large — max 25 MB per upload.');
      return;
    }
    try {
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      const safeKey = (tplById[upload.template_id]?.key || 'doc').replace(/[^a-z0-9_-]/gi, '_');
      const path = `${teamId}/${memberId}/${safeKey}_${Date.now()}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('client-docs')
        .upload(path, file, { upsert: false, contentType: file.type || undefined });
      if (upErr) throw new Error('Upload failed: ' + upErr.message);

      const { data, error: updErr } = await supabase
        .from('document_uploads')
        .update({
          storage_path: path,
          original_filename: file.name,
          status: 'in_review',
          admin_notes: null, // Clear any previous rejection note
          reviewed_at: null,
          reviewed_by: null,
        })
        .eq('id', upload.id)
        .select()
        .single();
      if (updErr) throw updErr;

      setUploads(prev => prev.map(u => u.id === upload.id ? data : u));

      // Best-effort classify ping — throttled to share the global classify
      // concurrency limit with any in-flight batch uploads.
      api.classifyUploadThrottled({
        team_id: teamId,
        upload_id: upload.id,
        storage_path: path,
        suggested_template_id: upload.template_id,
      }).catch(e => console.warn('Re-classify ping failed:', e));
    } catch (err) {
      console.error('Replace failed:', err);
      toast.error('Could not replace file: ' + (err.message || 'unknown error'));
    }
  };

  // ── Render ──
  if (loading && uploads.length === 0 && !hasInProgress) {
    return (
      <>
        <PageHeader title="My documents" subtitle="Loading…" />
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Spinner size={20} color="var(--accent)" />
        </Card>
      </>
    );
  }

  const total = uploads.length;
  const needsAttention = buckets.unmatched.length + buckets.rejected.length;
  const tplOptions = templates.map(t => ({
    value: t.id,
    label: t.label,
    hint: t.category ? `${t.category}${t.required ? ' · required' : ''}` : (t.required ? 'required' : null),
  }));

  return (
    <>
      <PageHeader
        title="My documents"
        subtitle={
          total === 0 && missingTemplates.length === 0
            ? "You haven't uploaded any documents to this workspace yet."
            : total === 0
              ? `${missingTemplates.length} document${missingTemplates.length === 1 ? '' : 's'} pending upload`
              : `${total} document${total === 1 ? '' : 's'} · ${needsAttention + missingTemplates.length} need${(needsAttention + missingTemplates.length) === 1 ? 's' : ''} your attention`
        }
        actions={
          <Btn
            variant="primary"
            size="md"
            icon={Icon.upload}
            onClick={() => setUploadOpen(true)}
          >
            Upload documents
          </Btn>
        }
      />

      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      {/* In progress — the whole batch appears here the moment it's submitted
          (uploading → AI classifying), then each doc resolves into its section
          below. Client-side items cover the pre-insert window; DB rows cover
          the classifying window. */}
      {hasInProgress && (
        <Section
          title="In progress"
          count={pendingClientItems.length + buckets.inProgress.length}
          tone="info"
          icon={Icon.upload}
          style={{ marginBottom: sectionGap }}
        >
          {pendingClientItems.map(b => (
            <InProgressRow
              key={b.id}
              name={b.file?.name || 'Document'}
              phase={b.status === 'failed' ? 'failed' : b.status === 'uploading' ? 'uploading' : 'queued'}
              error={b.error}
              onRetry={() => batchApiRef.current?.retry?.(b.id)}
              onDismiss={() => batchApiRef.current?.dismiss?.(b.id)}
            />
          ))}
          {buckets.inProgress.map(u => (
            <InProgressRow
              key={u.id}
              name={u.original_filename || u.display_name || 'Document'}
              phase="classifying"
            />
          ))}
        </Section>
      )}

      {/* Pending (role-required templates not yet uploaded) — always visible if any */}
      {missingTemplates.length > 0 && (
        <Section
          title="Pending"
          count={missingTemplates.length}
          tone="warning"
          icon={Icon.clock}
        >
          {missingTemplates.map(t => (
            <MissingRow
              key={t.id}
              template={t}
              onUpload={() => setUploadOpen(true)}
            />
          ))}
        </Section>
      )}

      {total === 0 && missingTemplates.length === 0 && !hasInProgress ? (
        <EmptyState team={team} onUpload={() => setUploadOpen(true)} />
      ) : total === 0 && !hasInProgress ? (
        /* Pending is shown above, but show a softer CTA below it */
        <div style={{ marginTop: 24, padding: 20, textAlign: 'center', background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 8 }}>
            No documents uploaded yet. Use the section above to get started.
          </div>
        </div>
      ) : (
        <>
          {buckets.unmatched.length > 0 && (
            <Section
              title="Unmatched"
              count={buckets.unmatched.length}
              tone="warning"
              icon={Icon.alert}
              style={{ marginTop: missingTemplates.length > 0 ? sectionGap : 0 }}
            >
              {buckets.unmatched.map(u => (
                <DocRow
                  key={u.id}
                  upload={u}
                  template={null}
                  templates={templates}
                  tplOptions={tplOptions}
                  onClassify={(tid) => classify(u.id, tid)}
                  onView={() => setViewing(u)}
                  onDelete={() => remove(u)}
                  onReplace={(file) => replace(u, file)}
                  onUpdate={(updated) => setUploads(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  variant="unmatched"
                />
              ))}
            </Section>
          )}

          {buckets.rejected.length > 0 && (
            <Section
              title="Rejected"
              count={buckets.rejected.length}
              tone="warning"
              icon={Icon.alert}
              style={{ marginTop: (missingTemplates.length > 0 || buckets.unmatched.length > 0) ? sectionGap : 0 }}
            >
              {buckets.rejected.map(u => (
                <DocRow
                  key={u.id}
                  upload={u}
                  template={tplById[u.template_id]}
                  templates={templates}
                  tplOptions={tplOptions}
                  onClassify={(tid) => classify(u.id, tid)}
                  onView={() => setViewing(u)}
                  onDelete={() => remove(u)}
                  onReplace={(file) => replace(u, file)}
                  onUpdate={(updated) => setUploads(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  variant="rejected"
                />
              ))}
            </Section>
          )}

          {buckets.inReview.length > 0 && (
            <Section
              title="In review"
              count={buckets.inReview.length}
              tone="info"
              icon={Icon.clock}
              style={{ marginTop: (missingTemplates.length > 0 || buckets.unmatched.length > 0 || buckets.rejected.length > 0) ? sectionGap : 0 }}
            >
              {buckets.inReview.map(u => (
                <DocRow
                  key={u.id}
                  upload={u}
                  template={tplById[u.template_id]}
                  templates={templates}
                  tplOptions={tplOptions}
                  onClassify={(tid) => classify(u.id, tid)}
                  onView={() => setViewing(u)}
                  onDelete={() => remove(u)}
                  onReplace={(file) => replace(u, file)}
                  onUpdate={(updated) => setUploads(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  variant="in_review"
                />
              ))}
            </Section>
          )}

          {buckets.approved.length > 0 && (
            <Section
              title="Approved"
              count={buckets.approved.length}
              tone="success"
              icon={Icon.check}
              style={{ marginTop: (missingTemplates.length > 0 || buckets.unmatched.length > 0 || buckets.rejected.length > 0 || buckets.inReview.length > 0) ? sectionGap : 0 }}
            >
              {buckets.approved.map(u => (
                <DocRow
                  key={u.id}
                  upload={u}
                  template={tplById[u.template_id]}
                  templates={templates}
                  tplOptions={tplOptions}
                  onClassify={(tid) => classify(u.id, tid)}
                  onView={() => setViewing(u)}
                  onDelete={() => remove(u)}
                  onReplace={(file) => replace(u, file)}
                  onUpdate={(updated) => setUploads(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  variant="approved"
                />
              ))}
            </Section>
          )}
        </>
      )}

      {viewing && (
        <FileViewerModal
          doc={viewing}
          templates={templates}
          canReview={false}  // members can't approve their own docs
          onClose={() => setViewing(null)}
          onChanged={(updated) => {
            setUploads(prev => prev.map(u => u.id === updated.id ? updated : u));
          }}
        />
      )}

      {/* Always mounted (visible toggles rendering) so the background batch
          keeps uploading after the modal instant-closes on file selection. */}
      {team && (
        <QuickUploadModal
          visible={uploadOpen}
          instantClose
          onBatchChange={setBatchItems}
          onBatchApi={(api) => { batchApiRef.current = api; }}
          teams={team ? [team] : []}
          lockedTeamId={teamId}
          onClose={() => setUploadOpen(false)}
          onUploaded={(newUpload) => {
            // Optimistically prepend so the "AI classifying" badge renders
            // immediately without waiting for the refresh roundtrip.
            if (newUpload) {
              setUploads(prev => [newUpload, ...prev.filter(u => u.id !== newUpload.id)]);
            }
            // Also refresh from DB shortly after to pick up server changes.
            setTimeout(() => refresh(), 500);
          }}
          onClassifyError={(errData) => {
            // Backend now returns enriched payload: { error, plan, used, limit, type }
            // AI-limit 403: errData.error === 'limit_reached', type defaults to 'ai'
            // Storage-limit 403: errData.error === 'storage_limit', type = 'storage'
            if (errData?.error === 'limit_reached' || errData?.error === 'storage_limit') {
              setLimitHit({
                type: errData.type || (errData.error === 'storage_limit' ? 'storage' : 'ai'),
                used: errData.used ?? 0,
                limit: errData.limit ?? 30,
                plan: errData.plan || 'free',
              });
            }
          }}
        />
      )}

      {limitHit && (
        <LimitReachedModal
          type={limitHit.type}
          used={limitHit.used}
          limit={limitHit.limit}
          plan={limitHit.plan}
          onClose={() => setLimitHit(null)}
          onUpgrade={() => {
            setLimitHit(null);
            window.location.href = '/account';
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// One row of the "In progress" section: a batch file mid-upload (client-side)
// or an inserted doc the AI is still classifying (DB row). Resolves out of the
// section automatically — no actions besides retry/dismiss on failures.
function InProgressRow({ name, phase, error, onRetry, onDismiss }) {
  const label = {
    queued:      'Waiting to upload…',
    uploading:   'Uploading…',
    classifying: 'AI is classifying…',
    failed:      error || 'Upload failed',
  }[phase] || phase;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', marginBottom: 8,
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
    }}>
      {phase === 'failed'
        ? <Icon.alert size={15} stroke="var(--danger)" />
        : <Spinner size={14} color={phase === 'classifying' ? 'var(--accent)' : 'var(--text-3)'} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </div>
        <div style={{ fontSize: 11.5, color: phase === 'failed' ? 'var(--danger)' : 'var(--text-3)' }}>
          {label}
        </div>
      </div>
      {phase === 'failed' && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Btn variant="ghost" size="xs" onClick={onRetry}>Retry</Btn>
          <Btn variant="ghost" size="xs" onClick={onDismiss}>Dismiss</Btn>
        </div>
      )}
    </div>
  );
}

function Section({ title, count, tone, icon: I, children, style }) {
  const isMobile = useIsMobile();
  const colorMap = {
    warning: { bg: 'var(--warn-bg)',  fg: 'var(--warn)'  },
    info:    { bg: 'var(--info-bg)',  fg: 'var(--info)'  },
    success: { bg: 'var(--ok-bg)',    fg: 'var(--ok)'    },
  };
  const c = colorMap[tone] || colorMap.info;

  return (
    <div style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10,
        marginBottom: isMobile ? 8 : 10,
        paddingBottom: isMobile ? 6 : 8,
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          width: isMobile ? 22 : 28, height: isMobile ? 22 : 28, borderRadius: isMobile ? 6 : 8,
          background: c.bg, color: c.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <I size={isMobile ? 12 : 14} />
        </div>
        <div style={{
          flex: 1, fontSize: isMobile ? 13 : 14,
          fontWeight: 700, letterSpacing: '-0.005em',
        }}>
          {title}
        </div>
        <Badge variant="neutral" size="sm">{count}</Badge>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {children}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function DocRow({ upload, template, templates, tplOptions, onClassify, onView, onDelete, onReplace, onUpdate, variant }) {
  const isMobile = useIsMobile();
  const { teamId } = useWorkspace();
  const fileInputRef = useRef(null);
  const ext = ((upload.original_filename || upload.storage_path || '').split('.').pop() || '').toLowerCase();

  // "AI is classifying" heuristic: unmatched + uploaded within the last ~45s.
  // Force a re-render by ticking every few seconds so the badge auto-clears.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (variant !== 'unmatched') return;
    const ageSec = (Date.now() - new Date(upload.created_at).getTime()) / 1000;
    if (ageSec > 60) return;
    const t = setInterval(() => forceTick(n => n + 1), 2000);
    return () => clearInterval(t);
  }, [variant, upload.created_at]);

  const ageSec = (Date.now() - new Date(upload.created_at).getTime()) / 1000;
  const aiClassifying = variant === 'unmatched' && ageSec < 45;

  const variantStyles = {
    unmatched:  { borderColor: 'var(--warn)',   accent: 'var(--warn)',   label: 'Needs a category' },
    rejected:   { borderColor: 'var(--danger)', accent: 'var(--danger)', label: 'Rejected — please replace' },
    in_review:  { borderColor: 'var(--border)', accent: 'var(--info)',   label: 'Waiting for staff review' },
    approved:   { borderColor: 'var(--border)', accent: 'var(--ok)',     label: 'Approved' },
  };
  const v = variantStyles[variant] || variantStyles.in_review;

  const badgeVariant =
    aiClassifying ? 'info' :
    variant === 'approved' ? 'ok' :
    variant === 'rejected' ? 'danger' :
    variant === 'unmatched' ? 'warn' : 'info';

  // Hidden file input shared by both mobile + desktop replace handlers
  const hiddenFileInput = (
    <input
      type="file"
      ref={fileInputRef}
      style={{ display: 'none' }}
      accept="image/*,application/pdf"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onReplace(file);
        e.target.value = '';
      }}
    />
  );

  // ── Mobile: elegant flat layout ──────────────────────────────────────
  if (isMobile) {
    const shortLabel =
      aiClassifying ? 'AI…' :
      variant === 'unmatched' ? 'Unmatched' :
      variant === 'rejected' ? 'Rejected' :
      variant === 'in_review' ? 'Pending' :
      variant === 'approved' ? 'Approved' : v.label;

    return (
      <div style={{
        padding: 12,
        borderBottom: '1px solid var(--border)',
      }}>
        {/* Header: icon + title + small status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileIcon ext={ext} compact />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13.5, fontWeight: 600, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: 1.25,
            }}>
              {template?.label || 'Unclassified'}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginTop: 1,
            }}>
              {upload.original_filename || 'Untitled'} · {timeAgo(upload.created_at)}
            </div>
          </div>
          <Badge variant={badgeVariant} size="xs">{shortLabel}</Badge>
        </div>

        {/* Inline rejection note — no box, just colored text under the title */}
        {variant === 'rejected' && upload.admin_notes && (
          <div style={{
            fontSize: 12, color: 'var(--danger)',
            marginTop: 8, paddingLeft: 40, lineHeight: 1.4,
            display: 'flex', gap: 6, alignItems: 'flex-start',
          }}>
            <Icon.alert size={12} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{upload.admin_notes}</span>
          </div>
        )}

        {/* Classification failed — only shown when the new schema field is set */}
        {upload.classification_status === 'classification_failed' && (
          <div style={{ marginTop: 8, paddingLeft: 40 }}>
            <RetryClassificationButton
              doc={upload}
              teamId={teamId}
              onUpdated={(updated) => onUpdate?.(updated)}
            />
          </div>
        )}

        {/* Inline AI classifying notice */}
        {variant === 'unmatched' && aiClassifying && (
          <div style={{
            fontSize: 12, color: 'var(--info)',
            marginTop: 8, paddingLeft: 40,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Spinner size={11} color="var(--info)" />
            AI is classifying this document…
          </div>
        )}

        {/* Classify select for unmatched + not classifying */}
        {variant === 'unmatched' && !aiClassifying && (
          <div style={{ marginTop: 10, marginLeft: 40 }}>
            <SearchableSelect
              value={null}
              onChange={(tid) => tid && onClassify(tid)}
              options={tplOptions}
              placeholder="Pick a category…"
            />
          </div>
        )}

        {/* Combined meta row: expiration pill + change-category link, side by side */}
        {variant !== 'unmatched' && template && (template?.has_expiration || true) && (
          <div style={{
            marginTop: 8, paddingLeft: 40,
            display: 'flex', alignItems: 'center',
            gap: 10, flexWrap: 'wrap',
            fontSize: 11.5, color: 'var(--text-3)',
          }}>
            {template?.has_expiration && (
              <ExpirationField
                uploadId={upload.id}
                expiresAt={upload.expires_at}
                canEdit={true}
                onChanged={(newDate) => onUpdate?.({ ...upload, expires_at: newDate })}
                variant="compact"
              />
            )}
            <ReclassifyControl
              current={upload.template_id}
              tplOptions={tplOptions}
              onChange={(tid) => tid && tid !== upload.template_id && onClassify(tid)}
            />
          </div>
        )}

        {hiddenFileInput}

        {/* Ghost action row — light visual weight, flush left */}
        <div style={{
          display: 'flex', gap: 0, marginTop: 8,
          marginLeft: -8,  // pull buttons flush with the card content's left edge
          borderTop: '1px solid var(--border)',
          paddingTop: 4,
        }}>
          <Btn variant="ghost" size="sm" icon={Icon.eye} onClick={onView}>View</Btn>
          <Btn variant="ghost" size="sm" icon={Icon.refresh} onClick={() => fileInputRef.current?.click()}>Replace</Btn>
          <Btn variant="ghost" size="sm" icon={Icon.trash} onClick={onDelete} style={{ color: 'var(--danger)' }}>Delete</Btn>
        </div>
      </div>
    );
  }

  // ── Desktop: original layout (unchanged) ────────────────────────────
  return (
    <div style={{
      padding: 14,
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <FileIcon ext={ext} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 600, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            marginBottom: 2,
          }}>
            {template?.label || 'Unclassified'}
          </div>
          <div style={{
            fontSize: 11.5, color: 'var(--text-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            marginBottom: 2,
          }}>
            {upload.original_filename || 'Untitled file'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {timeAgo(upload.created_at)}
          </div>

          {variant === 'rejected' && upload.admin_notes && (
            <div style={{
              marginTop: 8, padding: '8px 10px',
              background: 'var(--danger-bg)',
              borderRadius: 'var(--r-sm)',
              fontSize: 12, color: 'var(--text-2)',
              borderLeft: '2px solid var(--danger)',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: 'var(--danger)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                marginBottom: 2,
              }}>
                Reason
              </div>
              {upload.admin_notes}
            </div>
          )}

          {/* Classification failed — only shown when the new schema field is set */}
          {upload.classification_status === 'classification_failed' && (
            <div style={{ marginTop: 8 }}>
              <RetryClassificationButton
                doc={upload}
                teamId={teamId}
                onUpdated={(updated) => onUpdate?.(updated)}
              />
            </div>
          )}

          {variant === 'unmatched' && aiClassifying && (
            <div style={{
              marginTop: 10, padding: '8px 10px',
              background: 'var(--info-bg)',
              borderRadius: 'var(--r-sm)',
              borderLeft: '2px solid var(--info)',
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: 'var(--text-2)',
            }}>
              <Spinner size={12} color="var(--info)" />
              <span>
                <strong style={{ color: 'var(--info)', fontWeight: 700 }}>AI is classifying…</strong>
                {' '}We'll auto-assign a category in a moment. If AI isn't sure, you'll be asked to pick one.
              </span>
            </div>
          )}

          {variant === 'unmatched' && !aiClassifying && (
            <div style={{ marginTop: 10, maxWidth: 360 }}>
              <SearchableSelect
                value={null}
                onChange={(tid) => tid && onClassify(tid)}
                options={tplOptions}
                placeholder="Pick a category…"
              />
            </div>
          )}

          {variant !== 'unmatched' && template && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text-3)' }}>
              Wrong category?{' '}
              <ReclassifyControl
                current={upload.template_id}
                tplOptions={tplOptions}
                onChange={(tid) => tid && tid !== upload.template_id && onClassify(tid)}
              />
            </div>
          )}

          {variant !== 'unmatched' && template?.has_expiration && (
            <div style={{ marginTop: 10 }}>
              <ExpirationField
                uploadId={upload.id}
                expiresAt={upload.expires_at}
                canEdit={true}
                onChanged={(newDate) => onUpdate?.({ ...upload, expires_at: newDate })}
                variant="inline"
              />
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4,
          alignItems: 'flex-end', flexShrink: 0,
        }}>
          <Badge variant={badgeVariant} size="xs">
            {aiClassifying ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon.zap size={10} strokeWidth={2.5} />
                AI classifying…
              </span>
            ) : v.label}
          </Badge>

          {hiddenFileInput}

          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <Btn variant="ghost" size="xs" icon={Icon.eye} onClick={onView} title="View file" />
            <Btn variant="ghost" size="xs" icon={Icon.refresh} onClick={() => fileInputRef.current?.click()} title="Replace file" />
            <Btn variant="ghost" size="xs" icon={Icon.trash} onClick={onDelete} title="Delete" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ReclassifyControl({ current, tplOptions, onChange }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: 'none', border: 'none',
          padding: isMobile ? '4px 0' : 0,
          color: 'var(--accent-600)', fontWeight: 600,
          cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit',
          textDecoration: 'underline',
        }}
      >
        Change category
      </button>
    );
  }
  return (
    <div style={{
      display: isMobile ? 'block' : 'inline-block',
      width: isMobile ? '100%' : undefined,
      minWidth: isMobile ? 0 : 240,
      marginTop: 4,
    }}>
      <SearchableSelect
        value={current}
        onChange={(tid) => { onChange(tid); setOpen(false); }}
        options={tplOptions}
        placeholder="Pick a different category…"
      />
    </div>
  );
}

function FileIcon({ ext, compact }) {
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext);
  const isPdf   = ext === 'pdf';
  const I = isImage ? Icon.file : isPdf ? Icon.docs : Icon.file;
  const bg = isImage ? 'var(--info-bg)' : isPdf ? 'var(--danger-bg)' : 'var(--surface-2)';
  const fg = isImage ? 'var(--info)' : isPdf ? 'var(--danger)' : 'var(--text-3)';
  const size = compact ? 30 : 36;
  return (
    <div style={{
      width: size, height: size, borderRadius: compact ? 7 : 8,
      background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, position: 'relative',
    }}>
      <I size={compact ? 13 : 15} />
      {ext && (
        <div style={{
          position: 'absolute', bottom: -3, right: -3,
          padding: '1px 4px', fontSize: 8, fontWeight: 700,
          background: fg, color: '#fff', borderRadius: 3,
          textTransform: 'uppercase', letterSpacing: '0.05em',
          minWidth: 16, textAlign: 'center', lineHeight: '10px',
        }}>
          {ext.substring(0, 4)}
        </div>
      )}
    </div>
  );
}

function EmptyState({ team, onUpload }) {
  return (
    <Card style={{ padding: 48, textAlign: 'center' }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: '0 auto 18px',
        background: 'var(--accent-bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon.upload size={28} stroke="var(--accent-600)" />
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.01em' }}>
        Ready when you are
      </div>
      <div style={{
        fontSize: 13, color: 'var(--text-3)',
        maxWidth: 380, margin: '0 auto 20px', lineHeight: 1.55,
      }}>
        Upload an employment form, certificate, ID, or any other document.
        We'll automatically classify it and route it to the right reviewer in {team?.name || 'your workspace'}.
      </div>
      <Btn variant="primary" size="md" icon={Icon.upload} onClick={onUpload}>
        Upload your first document
      </Btn>
    </Card>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)       return 'just now';
  if (diff < 3600)     return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)    return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400*7)  return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ────────────────────────────────────────────────────────────────────────────
// MissingRow — shown in "Pending" section for templates assigned to this
// user's role that they haven't uploaded yet
// ────────────────────────────────────────────────────────────────────────────
function MissingRow({ template, onUpload }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      padding: isMobile ? 12 : 14,
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 10 : 12,
      alignItems: isMobile ? 'stretch' : 'center',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12,
        flex: isMobile ? undefined : 1, minWidth: 0,
      }}>
        <div style={{
          width: isMobile ? 30 : 36, height: isMobile ? 30 : 36, borderRadius: 'var(--r-sm)',
          background: 'var(--warn-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon.upload size={isMobile ? 13 : 16} stroke="var(--warn)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: isMobile ? 13 : 13.5, fontWeight: 600, color: 'var(--text)',
            marginBottom: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {template.label}
          </div>
          <div style={{ fontSize: isMobile ? 11 : 11.5, color: 'var(--text-3)' }}>
            {template.required ? (
              <>
                <strong style={{ color: 'var(--warn)', fontWeight: 600 }}>Required</strong>
                {template.category ? ` · ${template.category}` : ''}
              </>
            ) : (
              template.category || 'Not yet uploaded'
            )}
          </div>
        </div>
      </div>
      <Btn
        variant="primary"
        size="sm"
        icon={Icon.upload}
        onClick={onUpload}
        style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
      >
        Upload now
      </Btn>
    </div>
  );
}
